export type RoleColors = {
    primaryColor?: number | null;
    primary_color?: number | null;
    secondaryColor?: number | null;
    secondary_color?: number | null;
    tertiaryColor?: number | null;
    tertiary_color?: number | null;
};

export type RoleColorSource = {
    color?: number | null;
    colors?: RoleColors | null;
    [key: string]: unknown;
};

export type NormalizedRoleColors = {
    primaryColor?: number | null;
    secondaryColor?: number | null;
    tertiaryColor?: number | null;
};

const DefaultRoleColor = "#747f8d";
const HolographicRoleColors = {
    primaryColor   : 11127295,
    secondaryColor : 16759788,
    tertiaryColor  : 16761760,
};

function isValidRoleColor(color: unknown): color is number {
    return typeof color === "number" && Number.isFinite(color) && color > 0;
}

export function roleColorToCss(color: number | null | undefined): string {
    if (!isValidRoleColor(color))
        return DefaultRoleColor;

    return `#${color.toString(16).padStart(6, "0")}`;
}

export function normalizeRoleColors(role: RoleColorSource): NormalizedRoleColors {
    const colors = role.colors;

    return {
        primaryColor   : colors?.primaryColor ?? colors?.primary_color ?? role.color ?? null,
        secondaryColor : colors?.secondaryColor ?? colors?.secondary_color ?? null,
        tertiaryColor  : colors?.tertiaryColor ?? colors?.tertiary_color ?? null,
    };
}

export function getRolePrimaryColor(role: RoleColorSource): number | null {
    const colors = normalizeRoleColors(role);

    return isValidRoleColor(colors.primaryColor) ? colors.primaryColor : null;
}

export function getRolePrimaryColorCss(role: RoleColorSource): string {
    return roleColorToCss(getRolePrimaryColor(role));
}

export function isHolographicRoleColor(role: RoleColorSource): boolean {
    const colors = normalizeRoleColors(role);

    return colors.primaryColor === HolographicRoleColors.primaryColor
        && colors.secondaryColor === HolographicRoleColors.secondaryColor
        && colors.tertiaryColor === HolographicRoleColors.tertiaryColor;
}

export function roleColorGradientToCss(role: RoleColorSource): string {
    const colors = normalizeRoleColors(role);
    const colorStops = [
        colors.primaryColor,
        colors.secondaryColor,
        colors.tertiaryColor,
    ].filter(isValidRoleColor);

    if (colorStops.length < 2)
        return roleColorToCss(colorStops[0]);

    if (isHolographicRoleColor(role)) {
        return [
            "linear-gradient(135deg,",
            "#a9f1ff 0%,",
            "#ffb0f4 32%,",
            "#fff6a8 66%,",
            "#a7ffdd 100%)",
        ].join(" ");
    }

    return `linear-gradient(135deg, ${colorStops.map(roleColorToCss).join(", ")})`;
}
